import { create } from 'zustand';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Types
export interface Professional {
  id: string;
  userId: string;
  type: string;
  title: string;
  bio?: string;
  hourlyRate?: number;
  rating: number;
  reviewCount: number;
  isAvailable: boolean;
  specialties: string[];
  languages: string[];
  verificationStatus?: 'pending' | 'approved' | 'rejected';
  user: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
}

export interface Hire {
  id: string;
  professionalId: string;
  authorId: string;
  jobId?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'active' | 'completed' | 'cancelled';
  agreedRate?: number;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  professional?: Professional;
  author?: { id: string; name: string; avatarUrl?: string };
  job?: JobPosting;
}

export interface JobPosting {
  id: string;
  authorId: string;
  projectId?: string;
  title: string;
  description?: string;
  type: string;
  budget?: number;
  deadline?: string;
  status: 'draft' | 'open' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  author?: { id: string; name: string; avatarUrl?: string };
  project?: { id: string; title: string };
}

interface MarketplaceState {
  // Professionals
  professionals: Professional[];
  selectedProfessional: Professional | null;
  isLoadingProfessionals: boolean;
  fetchProfessionals: (filters?: { type?: string; search?: string; available?: boolean }) => Promise<void>;
  selectProfessional: (professional: Professional | null) => void;

  // Hires
  sentHires: Hire[];
  receivedHires: Hire[];
  isLoadingHires: boolean;
  fetchSentHires: () => Promise<void>;
  fetchReceivedHires: () => Promise<void>;
  createHire: (professionalId: string, jobId?: string, agreedRate?: number) => Promise<Hire>;
  updateHireStatus: (hireId: string, status: Hire['status']) => Promise<void>;

  // Jobs
  jobs: JobPosting[];
  myJobs: JobPosting[];
  isLoadingJobs: boolean;
  fetchJobs: (filters?: { type?: string; status?: string }) => Promise<void>;
  fetchMyJobs: () => Promise<void>;
  createJob: (data: Partial<JobPosting>) => Promise<JobPosting>;
  updateJob: (jobId: string, updates: Partial<JobPosting>) => Promise<void>;
  deleteJob: (jobId: string) => Promise<void>;
}

export const useMarketplaceStore = create<MarketplaceState>((set, get) => ({
  // Professionals
  professionals: [],
  selectedProfessional: null,
  isLoadingProfessionals: false,

  fetchProfessionals: async (filters) => {
    set({ isLoadingProfessionals: true });
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.set('type', filters.type);
      if (filters?.search) params.set('search', filters.search);
      if (filters?.available) params.set('available', 'true');

      const response = await fetch(`${API_BASE}/professionals?${params}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        set({ professionals: data.professionals, isLoadingProfessionals: false });
      }
    } catch (error) {
      console.error('Failed to fetch professionals:', error);
      set({ isLoadingProfessionals: false });
    }
  },

  selectProfessional: (professional) => {
    set({ selectedProfessional: professional });
  },

  // Hires
  sentHires: [],
  receivedHires: [],
  isLoadingHires: false,

  fetchSentHires: async () => {
    set({ isLoadingHires: true });
    try {
      const response = await fetch(`${API_BASE}/professionals/hires/sent`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        set({ sentHires: data.hires, isLoadingHires: false });
      }
    } catch (error) {
      console.error('Failed to fetch sent hires:', error);
      set({ isLoadingHires: false });
    }
  },

  fetchReceivedHires: async () => {
    set({ isLoadingHires: true });
    try {
      const response = await fetch(`${API_BASE}/professionals/hires/received`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        set({ receivedHires: data.hires, isLoadingHires: false });
      }
    } catch (error) {
      console.error('Failed to fetch received hires:', error);
      set({ isLoadingHires: false });
    }
  },

  createHire: async (professionalId, jobId, agreedRate) => {
    const response = await fetch(`${API_BASE}/professionals/hires`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ professionalId, jobId, agreedRate }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create hire');
    }

    const data = await response.json();

    // Refresh sent hires
    get().fetchSentHires();

    return data.hire;
  },

  updateHireStatus: async (hireId, status) => {
    const response = await fetch(`${API_BASE}/professionals/hires/${hireId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update hire');
    }

    // Refresh hires
    get().fetchSentHires();
    get().fetchReceivedHires();
  },

  // Jobs
  jobs: [],
  myJobs: [],
  isLoadingJobs: false,

  fetchJobs: async (filters) => {
    set({ isLoadingJobs: true });
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.set('type', filters.type);
      if (filters?.status) params.set('status', filters.status);

      const response = await fetch(`${API_BASE}/professionals/jobs?${params}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        set({ jobs: data.jobs, isLoadingJobs: false });
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      set({ isLoadingJobs: false });
    }
  },

  fetchMyJobs: async () => {
    set({ isLoadingJobs: true });
    try {
      const response = await fetch(`${API_BASE}/professionals/jobs?mine=true`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        set({ myJobs: data.jobs, isLoadingJobs: false });
      }
    } catch (error) {
      console.error('Failed to fetch my jobs:', error);
      set({ isLoadingJobs: false });
    }
  },

  createJob: async (data) => {
    const response = await fetch(`${API_BASE}/professionals/jobs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create job');
    }

    const result = await response.json();

    // Refresh jobs
    get().fetchMyJobs();

    return result.job;
  },

  updateJob: async (jobId, updates) => {
    const response = await fetch(`${API_BASE}/professionals/jobs/${jobId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update job');
    }

    // Refresh jobs
    get().fetchMyJobs();
  },

  deleteJob: async (jobId) => {
    const response = await fetch(`${API_BASE}/professionals/jobs/${jobId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete job');
    }

    // Refresh jobs
    get().fetchMyJobs();
  },
}));
